import { SelectionModel } from '@angular/cdk/collections';
import { HttpErrorResponse } from '@angular/common/http';
import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
  MatNoDataRow,
} from '@angular/material/table';
import { Router, RouterLink } from '@angular/router';
import { catchError, tap, throwError, of, Subject, takeUntil } from 'rxjs';
import {
  Adherent,
  LinkAdherentToUser,
  UserAdherents,
} from 'src/app/shared/interfaces/cockpitAD/cockpitAD.model';
import { UpperCasePipe } from '@angular/common';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatButton, MatIconButton } from '@angular/material/button';
import { FormsModule } from '@angular/forms';
import { MatInput } from '@angular/material/input';
import {
  MatFormField,
  MatPrefix,
  MatSuffix,
} from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatToolbar } from '@angular/material/toolbar';
import { UserKeycloak } from 'src/app/shared/interfaces/keycloak/userKeycloak';
import { AdherentService } from 'src/app/shared/services/adherent.service';

@Component({
  selector: 'app-link-adherent',
  templateUrl: './link-adherent.component.html',
  styleUrl: './link-adherent.component.scss',
  standalone: true,
  imports: [
    MatToolbar,
    RouterLink,
    MatIcon,
    MatFormField,
    MatPrefix,
    MatInput,
    FormsModule,
    MatButton,
    MatIconButton,
    MatSuffix,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    MatCheckbox,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatNoDataRow,
    MatPaginator,
    UpperCasePipe,
  ],
})
export class LinkAdherentComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly _destroying$ = new Subject<void>();
  // Table
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  dataSource = new MatTableDataSource<Adherent>();
  displayedColumns: string[] = ['nom', 'codePostal', 'ville', 'actions'];
  // Filter
  globalFilter = '';
  // list adhérent B2C
  keycloakUser!: UserKeycloak;
  listAdherents?: Adherent[];
  listUserAdherentByUser?: UserAdherents[];
  adherentsSelected = new SelectionModel<Adherent>(true, []);
  // Router data
  undefined = 'undefined';
  routerData: any;

  constructor(
    private readonly router: Router,
    private readonly AdherentService: AdherentService
  ) {
    this.routerData = this.router.getCurrentNavigation()?.extras.state;
  }

  ngOnInit(): void {
    this.onTabSelected(2);
    if (typeof this.routerData === this.undefined) {
      this.router.navigate(['/pfa-interne/']);
    } else {
      this.keycloakUser = this.routerData.data as UserKeycloak;
      this.fetchAllAdherent();
      this.fetchUserAdherentByUser(this.keycloakUser.email);
    }
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  /**
   * Called when the component is destroyed. Unsubscribes from all subscriptions to prevent memory leaks.
   */
  ngOnDestroy() {
    this._destroying$.next();
    this._destroying$.complete();
  }

  /**
   * Retrieves the list of all B2C suppliers via the supplierService service.
   * The data is stored in the listLournisseursBC2 property.
   */
  private fetchAllAdherent() {
    this.AdherentService.fetchAllAdherent()
      .pipe(
        takeUntil(this._destroying$),
        tap((data: Adherent[]) => {
          this.listAdherents = data;
          this.manageDataSourceTable(this.listAdherents);
          this.selectAdherentsFromSameGroup();
        })
      )
      .subscribe();
  }

  /**
   * Retrieves the list of all UserAdherents By idAdherent via the cockpitADService service.
   * The data is stored in the listUserAdherentByAdherent property.
   * Errors are caught and returned as an Observable with throwError().
   */
  private fetchUserAdherentByUser(email: string) {
    this.AdherentService.fetchUserAdherentByUser(email)
      .pipe(
        takeUntil(this._destroying$),
        tap((data: UserAdherents[]) => {
          this.listUserAdherentByUser = data;
          this.selectAdherentsFromSameGroup();
        }),
        catchError((httpErrorResponse: HttpErrorResponse) => {
          if (httpErrorResponse.status === 404) {
            this.listUserAdherentByUser = [];
            return of([]);
          } else {
            const error = httpErrorResponse;
            return throwError(() => error);
          }
        })
      )
      .subscribe();
  }

  /**
   * Saves the selected rows as userAdherents for the current adherent.
   * Transforms the selected rows to Adherents format and sends the data to the server.
   */
  onSave() {
    const selectedRows: Adherent[] = this.adherentsSelected.selected;
    const oldData: UserAdherents[] = this
      .listUserAdherentByUser as UserAdherents[];
    const newToSave: UserAdherents[] =
      this.transformAdherentsToUserAdherents(selectedRows);
    if (oldData === undefined) {
      this.saveLinkAdherentToUser(newToSave);
    } else {
      const { additions, deletions } = this.getAdditionsAndDeletions(
        oldData,
        newToSave
      );
      this.deleteLinkAdherentToUser(deletions);
      this.saveLinkAdherentToUser(additions);
    }
  }

  /**
   * Returns an object with two arrays, containing the additions and deletions between the provided old and new objects.
   * @param {UserAdherents[]} oldObjects - The array of old objects.
   * @param {UserAdherents[]} newObjects - The array of new objects.
   * @returns {{ additions: UserAdherents[]; deletions: UserAdherents[] }} - An object with two properties:
   * "additions" containing the new objects that were not present in the oldObjects array,
   * and "deletions" containing the old objects that were not present in the newObjects array.
   */
  getAdditionsAndDeletions(
    oldObjects: UserAdherents[],
    newObjects: UserAdherents[]
  ): { additions: UserAdherents[]; deletions: UserAdherents[] } {
    const additions: UserAdherents[] = [];
    const deletions: UserAdherents[] = [];
    for (const oldObject of oldObjects) {
      if (
        !newObjects.find(
          (newObject) =>
            oldObject.idUser === newObject.idUser &&
            oldObject.idAdherent === newObject.idAdherent
        )
      ) {
        deletions.push(oldObject);
      }
    }
    for (const newObject of newObjects) {
      if (
        !oldObjects.find(
          (oldObject) =>
            newObject.idUser === oldObject.idUser &&
            newObject.idAdherent === oldObject.idAdherent
        )
      ) {
        additions.push(newObject);
      }
    }
    return { additions, deletions };
  }

  /**
   * Transforms an array of "Adherents" objects by mapping each object to a new UserAdherents object
   * @param data An array of "Adherents" objects to transform
   * @param idAdherent ID
   * @returns An array of transformed objects with the specified properties
   */
  private transformAdherentsToUserAdherents(data: Adherent[]): UserAdherents[] {
    return data.map((adherent: Adherent) => ({
      id: adherent.idAdherent,
      idUser: this.keycloakUser.id,
      adresseMail: this.keycloakUser.email,
      groupUser: 1,
      idAdherent: adherent.idAdherent,
    }));
  }

  /**
   * Transforms an array of "UserAdherents" objects by mapping each object to a new LinkAdherentToB2C object
   * @param data An array of "UserAdherents" objects to transform
   * @returns An array of transformed objects with the specified properties
   */
  private transformUserAdherentsToLinkAdherentToUser(
    data: UserAdherents[]
  ): LinkAdherentToUser[] {
    return data.map((userAdherent: UserAdherents) => ({
      idUser: userAdherent.idUser,
      idAdherent: userAdherent.idAdherent,
      adresseMail: userAdherent.adresseMail,
      groupUser: 1,
    }));
  }

  /**
   * Save the list of all B2C suppliers via the cockpitADService service.
   * @param additions The array of selected UserAdherents to add
   */
  private saveLinkAdherentToUser(additions: UserAdherents[]) {
    const dataToSave: LinkAdherentToUser[] =
      this.transformUserAdherentsToLinkAdherentToUser(additions);
    this.AdherentService.linkAdherentToUser(dataToSave)
      .pipe(takeUntil(this._destroying$))
      .subscribe();
  }

  /**
   * Sends a request to the "deleteAdherentsToB2C" method of the "cockpitADService" to delete the specified array of UserAdherents
   * @param deletions An array of "UserAdherents" objects to delete
   */
  private deleteLinkAdherentToUser(deletions: UserAdherents[]) {
    const dataToDelete: LinkAdherentToUser[] =
      this.transformUserAdherentsToLinkAdherentToUser(deletions);
    this.AdherentService.deleteAdherentsToUser(dataToDelete)
      .pipe(takeUntil(this._destroying$))
      .subscribe();
  }

  /**
   * Sets the array of data as the source for a data table called "dataSource".
   * @param data An array of data to set as the source for the data table.
   */
  private manageDataSourceTable(data: Adherent[]) {
    if (data) {
      this.dataSource.data = data;
    }
  }

  /**
   * Handles the selection of an element by toggling its state.
   * @param element The element to select/deselect.
   */
  selectHandler(element: Adherent) {
    this.adherentsSelected.toggle(element);
  }

  /**
   * Selects the Adherents suppliers associated with users from the same group as the current user.
   * The selected suppliers will be stored in the adherentsSelected property.
   * @returns void
   */
  private selectAdherentsFromSameGroup() {
    if (this.listUserAdherentByUser && this.listAdherents) {
      for (const adherent of this.listAdherents) {
        const user = this.listUserAdherentByUser.find(
          (u) => u.idAdherent === adherent.idAdherent
        );
        if (user) {
          this.adherentsSelected.toggle(adherent);
        }
      }
    }
  }

  /**
   * Whether the number of selected elements matches the total number of rows.
   */
  isAllSelected() {
    const numSelected = this.adherentsSelected.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  /**
   * Selects all rows if they are not all selected; otherwise clear selection.
   */
  masterToggle() {
    this.isAllSelected()
      ? this.adherentsSelected.clear()
      : this.dataSource.data.forEach((row) =>
          this.adherentsSelected.select(row)
        );
  }

  /**
   * Manages the filters to apply for the search bar
   * If the table has pagination then the pagination is placed on the first page
   * @param filter The value of the applied filter
   */
  applyFilter(filter: string) {
    this.globalFilter = filter;
    this.dataSource.filter = this.globalFilter;

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  /**
   * Highlights the selected tab and sets the others to the default state
   * @param {number} index - The index of the selected tab
   */
  onTabSelected(index: number): void {
    const menuElements = document.querySelectorAll('.menu-element');
    if (menuElements.length > 3 && index >= 0 && index < menuElements.length) {
      for (let i = 0; i < menuElements.length; i++) {
        const menuElement = menuElements[i] as HTMLElement;
        if (i === index) {
          menuElement.classList.remove('menu-element--default');
          menuElement.classList.add('menu-element--highlighted');
        } else {
          menuElement.classList.remove('menu-element--highlighted');
          menuElement.classList.add('menu-element--default');
        }
      }
    }
  }
}
